import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import NextImage from '@/components/media/StorefrontImage';
import { toStorefrontMediaUrl } from '@/lib/media-url';
import type { HeroBannerBlock } from '../types';
import { RichSegments } from './RichSegments';
import styles from './HeroBanner.module.css';
interface HeroBannerProps {
  block: HeroBannerBlock;
  blockId: string;
}
const DEFAULT_GRADIENT = 'linear-gradient(to top right, rgba(27, 80, 120, 0.6), rgba(235, 131, 34, 0.6))';
export function HeroBanner(t0) {
  const $ = _c(43);
  const {
    block,
    blockId
  } = t0;
  const ctaPlacement = block.ctaPlacement ?? "inline";
  const overlay = block.gradientOverlay ?? DEFAULT_GRADIENT;
  let t1;
  if ($[0] !== block.backgroundImage || $[1] !== overlay) {
    t1 = block.backgroundImage ? `${overlay}, url('${toStorefrontMediaUrl(block.backgroundImage)}')` : undefined;
    $[0] = block.backgroundImage;
    $[1] = overlay;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = block.minHeight ?? "500px";
  let t3;
  if ($[3] !== block.backgroundColor || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      backgroundImage: t1,
      backgroundColor: block.backgroundColor,
      minHeight: t2
    };
    $[3] = block.backgroundColor;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const inlineStyle = t3;
  const alignClass = block.align === "center" ? styles.centerAlign : styles.leftAlign;
  const layoutClass = block.layout === "split" ? styles.splitHero : null;
  let t4;
  if ($[7] !== alignClass || $[8] !== layoutClass) {
    t4 = [styles.hero, alignClass, layoutClass].filter(Boolean);
    $[7] = alignClass;
    $[8] = layoutClass;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const className = t4.join(" ");
  let t5;
  if ($[10] !== block.breadcrumbs) {
    t5 = block.breadcrumbs.length > 0 ? <nav aria-label="Breadcrumb" className={styles.breadcrumbs}><ol className={styles.breadcrumbList}>{block.breadcrumbs.map(_temp)}</ol></nav> : null;
    $[10] = block.breadcrumbs;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== block.eyebrow) {
    t6 = block.eyebrow ? <span className={styles.eyebrow}>{block.eyebrow}</span> : null;
    $[12] = block.eyebrow;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = block.headingLevel ?? 1;
  let t8;
  if ($[14] !== block.heading || $[15] !== t7) {
    t8 = <HeroHeading level={t7}>{block.heading}</HeroHeading>;
    $[14] = block.heading;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== block || $[18] !== ctaPlacement) {
    t9 = block.subtitle && block.subtitle.length > 0 ? <p className={styles.subtitle} data-cms-field="paragraph"><RichSegments segments={block.subtitle} />{block.cta && ctaPlacement === "inline" ? <><br /><br />{" "}<CtaLink block={block} inline={true} /></> : null}</p> : null;
    $[17] = block;
    $[18] = ctaPlacement;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== block.body) {
    t10 = block.body?.map(_temp2);
    $[20] = block.body;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== block || $[23] !== ctaPlacement) {
    t11 = block.cta && (!block.subtitle || block.subtitle.length === 0 || ctaPlacement === "below") ? <CtaLink block={block} /> : null;
    $[22] = block;
    $[23] = ctaPlacement;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t5 || $[28] !== t6 || $[29] !== t8 || $[30] !== t9) {
    t12 = <div className={styles.copy}>{t5}{t6}{t8}{t9}{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t5;
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== block.mediaImage) {
    t13 = block.mediaImage ? <div className={styles.media}>{block.mediaImage.width && block.mediaImage.height ? <NextImage src={block.mediaImage.src} alt={block.mediaImage.alt} width={block.mediaImage.width} height={block.mediaImage.height} className={styles.mediaImage} priority={true} /> : <img src={toStorefrontMediaUrl(block.mediaImage.src)} alt={block.mediaImage.alt} className={styles.mediaImage} loading="eager" />}</div> : null;
    $[32] = block.mediaImage;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12 || $[35] !== t13) {
    t14 = <div className={styles.container}>{t12}{t13}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== block.id || $[38] !== blockId || $[39] !== className || $[40] !== inlineStyle || $[41] !== t14) {
    t15 = <section id={block.id} className={className} style={inlineStyle} data-cms-block-id={blockId} data-cms-field="heroBanner">{t14}</section>;
    $[37] = block.id;
    $[38] = blockId;
    $[39] = className;
    $[40] = inlineStyle;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
}
function _temp2(paragraph, idx_0) {
  return <p key={idx_0} className={styles.bodyParagraph} data-cms-field="paragraph"><RichSegments segments={paragraph} /></p>;
}
function _temp(crumb, idx) {
  return <li key={idx} className={styles.breadcrumbItem}>{crumb.href && !crumb.current ? <a href={crumb.href} className={styles.breadcrumbLink}>{crumb.label}</a> : <strong className={styles.breadcrumbCurrent}>{crumb.label}</strong>}</li>;
}
function HeroHeading(t0) {
  const $ = _c(8);
  const {
    level,
    children
  } = t0;
  switch (level) {
    case 2:
      {
        let t1;
        if ($[0] !== children) {
          t1 = <h2 className={styles.heading}>{children}</h2>;
          $[0] = children;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case 3:
      {
        let t1;
        if ($[2] !== children) {
          t1 = <h3 className={styles.heading}>{children}</h3>;
          $[2] = children;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case 4:
      {
        let t1;
        if ($[4] !== children) {
          t1 = <h4 className={styles.heading}>{children}</h4>;
          $[4] = children;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case 1:
    default:
      {
        let t1;
        if ($[6] !== children) {
          t1 = <h1 className={styles.heading}>{children}</h1>;
          $[6] = children;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
  }
}
function CtaLink(t0) {
  const $ = _c(10);
  const {
    block,
    inline: t1
  } = t0;
  const inline = t1 === undefined ? false : t1;
  if (!block.cta) {
    return null;
  }
  const t2 = `${styles.cta} ${block.cta.variant === "secondary" ? styles.ctaSecondary : styles.ctaPrimary}`;
  let t3;
  if ($[0] !== block.cta.label) {
    t3 = <span className={styles.ctaButton}>{block.cta.label}</span>;
    $[0] = block.cta.label;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== block.cta.href || $[3] !== block.cta.rel || $[4] !== block.cta.target || $[5] !== t2 || $[6] !== t3) {
    t4 = <a href={block.cta.href} target={block.cta.target} rel={block.cta.rel} className={t2}>{t3}</a>;
    $[2] = block.cta.href;
    $[3] = block.cta.rel;
    $[4] = block.cta.target;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const link = t4;
  if (inline) {
    return link;
  }
  let t5;
  if ($[8] !== link) {
    t5 = <p className={styles.ctaParagraph}>{link}</p>;
    $[8] = link;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}